import { useState } from 'react';
import { Card, Badge } from '../ui/components';

interface Snapshot {
  fingerprintHash: string;
  fingerprintParts: Record<string, string>;
  botScore: number;
  label: string;
}

/**
 * Compare two scans.
 *
 * Paste the `fingerprintParts` JSON (from `window.FP_SCAN.exportJson()` or the
 * compare view) for two environments and see exactly which parts differ. This is
 * the quickest way to see what a spoofing change actually moved.
 */
export function Compare() {
  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [error, setError] = useState<string | null>(null);

  const parse = (s: string): Snapshot | null => {
    if (!s.trim()) return null;
    try {
      const j = JSON.parse(s);
      if (!j || typeof j !== 'object') return null;
      return {
        fingerprintHash: j.fingerprintHash ?? '',
        fingerprintParts: j.fingerprintParts ?? {},
        botScore: j.botScore ?? 0,
        label: j.label ?? '',
      };
    } catch {
      return null;
    }
  };

  const a = parse(left);
  const b = parse(right);

  const diff = (() => {
    if (!a || !b) return null;
    const parts = new Set([...Object.keys(a.fingerprintParts), ...Object.keys(b.fingerprintParts)]);
    const rows = [...parts].map((p) => ({
      part: p,
      left: a.fingerprintParts[p] ?? null,
      right: b.fingerprintParts[p] ?? null,
    }));
    return rows;
  })();

  const changed = diff?.filter((r) => r.left !== r.right) ?? [];

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight text-white">Compare two runs</h1>
        <p className="max-w-4xl text-sm leading-6 text-zinc-400">
          Paste the JSON from <code className="font-mono text-zinc-300">window.FP_SCAN</code> for
          two environments to see which fingerprint parts differ. Useful for verifying that a
          spoofing change moved the subsystem you intended, and nothing else.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Run A">
          <textarea
            value={left}
            onChange={(e) => {
              setLeft(e.target.value);
              setError(null);
            }}
            placeholder='{ "fingerprintHash": "...", "fingerprintParts": { ... } }'
            className="h-48 w-full resize-y rounded-lg border border-white/10 bg-black/40 p-3 font-mono text-xs leading-5 text-zinc-200 placeholder-zinc-600"
            spellCheck={false}
          />
        </Card>
        <Card title="Run B">
          <textarea
            value={right}
            onChange={(e) => {
              setRight(e.target.value);
              setError(null);
            }}
            placeholder='{ "fingerprintHash": "...", "fingerprintParts": { ... } }'
            className="h-48 w-full resize-y rounded-lg border border-white/10 bg-black/40 p-3 font-mono text-xs leading-5 text-zinc-200 placeholder-zinc-600"
            spellCheck={false}
          />
        </Card>
      </div>

      {error && <p className="text-sm text-rose-300">{error}</p>}

      {a && b && diff && (
        <Card
          title="Differences"
          subtitle={
            changed.length === 0
              ? 'No parts differ.'
              : `${changed.length} of ${diff.length} part(s) differ.`
          }
        >
          {a.fingerprintHash !== b.fingerprintHash && (
            <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm text-amber-100">
              Overall fingerprint hash differs, which follows from at least one part differing.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500">
                  <th className="py-2 pr-4 font-medium">part</th>
                  <th className="py-2 pr-4 font-medium">run A</th>
                  <th className="py-2 pr-4 font-medium">run B</th>
                  <th className="py-2 font-medium">changed</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {diff.map((r) => {
                  const isChanged = r.left !== r.right;
                  return (
                    <tr
                      key={r.part}
                      className={`border-b border-white/5 ${isChanged ? 'bg-orange-500/5' : ''}`}
                    >
                      <td className="py-1.5 pr-4 font-sans text-zinc-400">{r.part}</td>
                      <td className="py-1.5 pr-4 text-zinc-300">{r.left ?? '—'}</td>
                      <td className="py-1.5 pr-4 text-zinc-300">{r.right ?? '—'}</td>
                      <td className="py-1.5">
                        {isChanged ? <Badge tone="denied">changed</Badge> : <span className="text-zinc-600">same</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!a && !b && (
        <Card>
          <p className="text-sm text-zinc-500">
            Paste two JSON exports to compare. On a running scan, open the console and run{' '}
            <code className="font-mono text-zinc-300">copy(window.FP_SCAN.exportJson())</code>.
          </p>
        </Card>
      )}
    </div>
  );
}
